/**
 * Predefined delivery-area library (Blink-style named areas).
 *
 * The library is a static, OSM-derived dataset generated by
 * scripts/area-library/extract.py and checked in at
 * src/data/areaLibrary.isb-rwp.json. It is admin-editor-only: picking an area
 * copies its ring into the zone's `polygons`, so the backend and storefront
 * never load the library itself. Lazy-imported to keep it out of the main
 * bundle. ODbL attribution: show `attribution` wherever these polygons render.
 */

import type { LatLng } from '@/lib/deliveryZones'

export interface AreaLibraryEntry {
  slug: string
  name: string
  city: string
  aliases: string[]
  /** Grid-derived approximation rather than a mapped boundary. */
  approx?: boolean
  ring: LatLng[]
}

export interface AreaLibrary {
  version: number
  generated: string
  attribution: string
  license: string
  areas: AreaLibraryEntry[]
}

let cached: Promise<AreaLibrary> | null = null

export function loadAreaLibrary(): Promise<AreaLibrary> {
  cached ??= import('@/data/areaLibrary.isb-rwp.json').then((m) => m.default as AreaLibrary)
  return cached
}

/** "F 10", "f-10", "f10" all match "F-10"; aliases and city names count too. */
const norm = (s: string) => s.toLowerCase().replace(/[\s\-_/]+/g, '')

/**
 * Ranked search over the library: prefix matches on the normalized name come
 * first, then substring matches on name/aliases/city. Empty query returns
 * everything (grouped browsing).
 */
export function searchAreas(lib: AreaLibrary, query: string, limit = 30): AreaLibraryEntry[] {
  const q = norm(query)
  if (!q) return lib.areas.slice(0, limit)
  const scored: { entry: AreaLibraryEntry; score: number }[] = []
  for (const entry of lib.areas) {
    const name = norm(entry.name)
    const haystacks = [name, ...entry.aliases.map(norm), norm(entry.city)]
    let score = -1
    if (name.startsWith(q)) score = 0
    else if (haystacks.some((h) => h.startsWith(q))) score = 1
    else if (haystacks.some((h) => h.includes(q))) score = 2
    if (score >= 0) scored.push({ entry, score })
  }
  return scored
    .sort((a, b) => a.score - b.score || a.entry.name.localeCompare(b.entry.name))
    .slice(0, limit)
    .map((s) => s.entry)
}
